import React, { useState } from "react";

const inputStyle = {
  width: "100%",
  backgroundColor: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: "12px",
  padding: "14px 16px",
  fontFamily: "Inter, sans-serif",
  fontWeight: 400,
  fontSize: "15px",
  color: "#FFFFFF",
  outline: "none",
  lineHeight: "1.4",
};

const ContactContent = () => {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", message: "",
    jobTitle: "", businessName: "", businessAddress: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/zoho/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error("submission failed");
      if (typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", { event_category: "contact", event_label: "contact_form" });
      }
      setSubmitted(true);
    } catch {
      setError("something went wrong. please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full" style={{ backgroundColor: "#020013" }}>
      <div className="w-full px-4 md:px-12 lg:px-20 xl:px-28 py-16">
        <div className="flex flex-col lg:flex-row gap-10 items-center max-w-[1280px] mx-auto">

          {/* Left — Form */}
          <div
            className="w-full lg:w-[700px]"
            style={{
              backgroundColor: "rgba(26,21,35,0.6)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "32px",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              padding: "32px",
            }}
          >
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
                <div className="flex items-center justify-center w-16 h-16 rounded-full" style={{ backgroundColor: "rgba(110,63,198,0.2)" }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="#9747FF" strokeWidth={2}>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h3 className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "24px", color: "#FFFFFF" }}>thank you!</h3>
                <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "16px", color: "#CCC3DA", maxWidth: "360px" }}>
                  we've received your message and will be in touch within 3 to 5 working days.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                {/* Row 1: name + email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input type="text" name="name" placeholder="name*" value={formData.name} onChange={handleChange} required style={inputStyle} className="lowercase focus:ring-2 focus:ring-purple-500" />
                  <input type="email" name="email" placeholder="email*" value={formData.email} onChange={handleChange} required style={inputStyle} className="lowercase focus:ring-2 focus:ring-purple-500" />
                </div>

                {/* Row 2: phone */}
                <input type="tel" name="phone" placeholder="phone number*" value={formData.phone} onChange={handleChange} required style={inputStyle} className="lowercase focus:ring-2 focus:ring-purple-500" />

                {/* Row 3: job title + business name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input type="text" name="jobTitle" placeholder="job title (optional)" value={formData.jobTitle} onChange={handleChange} style={inputStyle} className="lowercase focus:ring-2 focus:ring-purple-500" />
                  <input type="text" name="businessName" placeholder="business name (optional)" value={formData.businessName} onChange={handleChange} style={inputStyle} className="lowercase focus:ring-2 focus:ring-purple-500" />
                </div>

                {/* Row 4: business address */}
                <input type="text" name="businessAddress" placeholder="business address or country (optional)" value={formData.businessAddress} onChange={handleChange} style={inputStyle} className="lowercase focus:ring-2 focus:ring-purple-500" />

                {/* Row 5: message */}
                <textarea name="message" placeholder="message" value={formData.message} onChange={handleChange} rows="5"
                  style={{ ...inputStyle, resize: "none" }} className="lowercase focus:ring-2 focus:ring-purple-500" />

                {error && <p className="lowercase text-red-400 text-sm">{error}</p>}

                <div className="flex justify-end mt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="lowercase transition-opacity hover:opacity-80 disabled:opacity-50"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "128px",
                      height: "52px",
                      flexShrink: 0,
                      borderRadius: "9999px",
                      backgroundColor: "#721AF0",
                      border: "none",
                      color: "#FFFFFF",
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 600,
                      fontSize: "16px",
                      cursor: "pointer",
                    }}
                  >
                    {loading ? "sending..." : "submit"}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Right — Contact info */}
          <div className="flex flex-col gap-6 lg:w-[460px] w-full">

            {/* Email */}
            <div className="flex items-start gap-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: "rgba(112,0,255,0.15)" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9747FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div>
                <p className="lowercase mb-1" style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: "13px", letterSpacing: "0.05em", color: "#CCC3DA" }}>email us</p>
                <p style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: "16px", color: "#FFFFFF" }}>sales@beemxyz.com</p>
              </div>
            </div>

            {/* HQ */}
            <div className="flex items-start gap-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: "rgba(112,0,255,0.15)" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9747FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
                <div>
                  <p className="lowercase mb-1" style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: "13px", letterSpacing: "0.05em", color: "#CCC3DA" }}>our headquarters</p>
                  <p style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: "16px", color: "#FFFFFF" }}>Northwall, Dublin, Ireland</p>
                </div>
            </div>

            {/* Talk to sales card */}
            <div
              style={{
                backgroundColor: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.05)",
                borderRadius: "32px",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                paddingTop: "48px",
                paddingRight: "32px",
                paddingBottom: "32px",
                paddingLeft: "32px",
                gap: "16px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "24px", lineHeight: "32px", letterSpacing: "0px", color: "#F3F4F6" }}>
                want to talk to sales?
              </p>
              <p className="lowercase" style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "14px", lineHeight: "1.6", color: "#CCC3DA" }}>
                schedule a quick discovery call with our expert team to explore tailored solutions for your enterprise.
              </p>
              <button
                onClick={() => window.location.href = "/business/contact"}
                className="w-full lowercase transition-opacity hover:opacity-80"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "60px",
                  borderRadius: "16px",
                  backgroundColor: "transparent",
                  border: "2px solid rgba(112,0,255,0.2)",
                  color: "#9747FF",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  fontSize: "15px",
                  cursor: "pointer",
                }}
              >
                book a demo →
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ContactContent;
